import { unwrap, type UserOut } from "@sagot/api-client";
import { NAME_MAX_LENGTH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Avatar, Button, Field, Input, toast } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../../lib/api-errors";
import { queryKeys } from "../../../../lib/query-keys";
import { zodResolver } from "../../../../lib/zod-resolver";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useMe } from "../../../../session/use-me";
import { CardActions, CardSkeleton, PanelError } from "./panel-kit";
import { PasswordCard } from "./password-card";
import { SessionsCard } from "./sessions-card";
import { TwoFactorCard } from "./two-factor-card";

const MOBILE = /^\+?[0-9 ()-]{7,20}$/;

function useProfileSchema() {
  const { t } = useTranslation();
  return z.object({
    name: z.string().trim().min(1, t("settings.profile.nameRequired")).max(NAME_MAX_LENGTH),
    job_title: z.string().trim().max(200),
    mobile: z.string().trim().refine((v) => v === "" || MOBILE.test(v), t("settings.profile.mobileInvalid")),
  });
}
type ProfileValues = z.infer<ReturnType<typeof useProfileSchema>>;

const toValues = (user: UserOut): ProfileValues => ({ name: user.name, job_title: user.job_title ?? "", mobile: user.mobile ?? "" });

function ProfileForm({ user }: { user: UserOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const readOnly = usePermissions().readOnly;
  const { register, handleSubmit, reset, formState } = useForm<ProfileValues>({ resolver: zodResolver(useProfileSchema()), defaultValues: toValues(user) });
  useEffect(() => {
    reset(toValues(user));
  }, [user, reset]);
  const onSubmit = handleSubmit(async (v) => {
    try {
      await unwrap(api.PATCH("/v1/me", { body: { name: v.name, job_title: v.job_title || null, mobile: v.mobile || null } }));
      toast.success(t("settings.profile.saved"));
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
    } catch (caught) {
      toast.error(message(caught));
    }
  });
  return (
    <form className="card" onSubmit={(event) => void onSubmit(event)} noValidate aria-labelledby="profile-title">
      <div className="card-header">
        <h2 id="profile-title" className="card-title">
          {t("settings.profile.title")}
        </h2>
      </div>
      <fieldset className="card-body space-y-5" disabled={readOnly}>
        <div className="flex items-center gap-4">
          <Avatar name={user.name} src={user.avatar_url ?? undefined} className="size-14 bg-brand-500 text-lg text-ink-950 dark:bg-brand-500 dark:text-ink-950" />
          <p className="text-xs text-fg-subtle">{t("settings.profile.photoHint")}</p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label={t("settings.profile.name")} error={formState.errors.name?.message}>
            <Input autoComplete="name" {...register("name")} />
          </Field>
          <Field label={t("settings.profile.jobTitle")} error={formState.errors.job_title?.message}>
            <Input autoComplete="organization-title" {...register("job_title")} />
          </Field>
          <Field label={t("settings.profile.email")} hint={t("settings.profile.emailHint")}>
            <Input type="email" readOnly value={user.email} className="bg-surface-2 text-fg-muted" />
          </Field>
          <Field label={t("settings.profile.mobile")} error={formState.errors.mobile?.message}>
            <Input type="tel" inputMode="tel" autoComplete="tel" {...register("mobile")} />
          </Field>
        </div>
      </fieldset>
      <CardActions>
        <Button type="submit" disabled={readOnly} loading={formState.isSubmitting}>
          {t("settings.profile.save")}
        </Button>
      </CardActions>
    </form>
  );
}

/** Profile panel: personal details, password, 2FA and active sessions (every member). */
export function ProfilePanel() {
  const me = useMe();
  if (me.isPending) return <CardSkeleton rows={4} />;
  if (me.isError) return <PanelError error={me.error} onRetry={() => void me.refetch()} />;
  return (
    <>
      <ProfileForm user={me.data.user} />
      <PasswordCard />
      <TwoFactorCard user={me.data.user} />
      <SessionsCard />
    </>
  );
}
